// What each "Import as" choice actually saves, built from a file's ImportPreview and its
// settings. Pure, so what lands in Recordings / Planned hikes / Waypoints is pinned by Jest
// (importPayloads.test.ts). Ported from Android's `ImportPayloads.kt`.

import type { ImportLine, ImportPoint, ImportPreview, ImportWaypoint } from './importTypes';
import { numberRange } from './importRules';

/** The settings these builders read (a subset of ImportFileSettings, to avoid a cycle). */
export interface PayloadSettings {
  joinDays: boolean;
  includeCourse: boolean;
  keepWaypoints: boolean;
  includeRepeats: boolean;
}

/** One planned-hike day: its points, in order, and the line's own name when it had one. */
export interface ImportDay {
  name: string | null;
  points: ImportPoint[];
}

const firstTime = (line: ImportLine): number => {
  for (const seg of line.segments) for (const p of seg) if (p.time != null) return p.time;
  return Number.MAX_SAFE_INTEGER;
};

/**
 * The tracks a recording is made of: the non-duplicate tracks whose times are usable
 * (contract rules 1 and 2), in time order — so several tracks in one file join into one
 * recording that runs forwards.
 */
export function recordingTracks(p: ImportPreview): ImportLine[] {
  return p.lines
    .filter(l => l.kind === 'track' && l.duplicateOf === null && l.timesUsable)
    .map((l, i) => ({ l, i, t: firstTime(l) }))
    .sort((a, b) => a.t - b.t || a.i - b.i)
    .map(({ l }) => l);
}

/** The recording's segments: every segment of every recording track, in time order. */
export const recordingSegments = (p: ImportPreview): ImportPoint[][] => recordingTracks(p).flatMap(l => l.segments);

/** Distance and elapsed time of what a recording would save. */
export const recordingDistanceM = (p: ImportPreview): number => recordingTracks(p).reduce((sum, l) => sum + l.distanceM, 0);

export function recordingDurationMs(p: ImportPreview): number {
  const times = recordingSegments(p).flat().map(pt => pt.time).filter((t): t is number => t != null);
  const range = times.length < 2 ? null : numberRange(times);
  return range ? Math.max(0, range.max - range.min) : 0;
}

/** The lines a planned hike is made of: a duplicate course only when asked for. */
export const plannedLines = (p: ImportPreview, s: PayloadSettings): ImportLine[] =>
  p.lines.filter(l => l.duplicateOf === null || s.includeCourse);

/** One day per line, or every line joined end to end into one day. */
export function plannedDays(p: ImportPreview, s: PayloadSettings): ImportDay[] {
  const lines = plannedLines(p, s);
  if (!lines.length) return [];
  if (s.joinDays && lines.length > 1) return [{ name: null, points: lines.flatMap(l => l.segments.flat()) }];
  return lines.map(l => ({ name: l.name, points: l.segments.flat() }));
}

/** The waypoints a file keeps without the user's say: everything that isn't a repeat. */
export const keptWaypoints = (p: ImportPreview): ImportWaypoint[] => p.waypoints.filter(w => !w.repeatsLine);

/** "Planned hike from waypoints": one day through the kept waypoints in file order. */
export const fromWaypointsDay = (p: ImportPreview): ImportDay => ({ name: null, points: keptWaypoints(p).map(w => w.point) });

/**
 * The waypoints that become highlights or a waypoint group: the named ones when
 * `keepWaypoints`, plus the line's repeats when `includeRepeats`.
 */
export const waypointsToAdd = (p: ImportPreview, s: PayloadSettings): ImportWaypoint[] =>
  p.waypoints.filter(w => (w.repeatsLine ? s.includeRepeats : s.keepWaypoints));

/** Every item, or an evenly spaced subset of at most `max` that keeps both ends. */
export function thinned<T>(items: T[], max: number): T[] {
  if (items.length <= max || max < 2) return items;
  const step = (items.length - 1) / (max - 1);
  const out: T[] = [];
  for (let i = 0; i < max - 1; i++) out.push(items[Math.min(Math.floor(i * step), items.length - 1)]);
  out.push(items[items.length - 1]);
  return out;
}
